/**
 * Theme Context for habitué
 * Provides dark/light mode toggle with localStorage persistence
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

// Theme configurations
export const themes = {
  dark: {
    name: 'dark',
    colors: {
      background: '#0F1A14',
      backgroundSecondary: '#1E2F26',
      backgroundTertiary: '#283D32',
      border: '#3E5248',
      text: '#E8EBE9',
      textMuted: '#8BA89A',
      textSecondary: '#6B7B73',
      gold: '#C3A365',
      goldHover: '#D4B87A',
      success: '#4CAF50',
      error: '#F44336',
      warning: '#FF9800',
    }
  },
  light: {
    name: 'light',
    colors: {
      background: '#F5F7F6',
      backgroundSecondary: '#FFFFFF',
      backgroundTertiary: '#E8EBE9',
      border: '#D1D9D5',
      text: '#0F1A14',
      textMuted: '#4A5A52',
      textSecondary: '#6B7B73',
      gold: '#8B7355',
      goldHover: '#6B5A45',
      success: '#2E7D32',
      error: '#C62828',
      warning: '#EF6C00',
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('habitue-theme');
      if (stored && (stored === 'dark' || stored === 'light')) {
        return stored;
      }
      // Check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
        return 'light';
      }
    }
    return 'dark'; // Default to dark (brand preference)
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    // Remove previous theme class
    root.classList.remove('theme-dark', 'theme-light');
    body.classList.remove('theme-dark', 'theme-light');
    
    // Add current theme class
    root.classList.add(`theme-${theme}`);
    body.classList.add(`theme-${theme}`);
    
    // Update CSS variables
    const colors = themes[theme].colors;
    root.style.setProperty('--habitue-bg', colors.background);
    root.style.setProperty('--habitue-bg-secondary', colors.backgroundSecondary);
    root.style.setProperty('--habitue-bg-tertiary', colors.backgroundTertiary);
    root.style.setProperty('--habitue-border', colors.border);
    root.style.setProperty('--habitue-text', colors.text);
    root.style.setProperty('--habitue-text-muted', colors.textMuted);
    root.style.setProperty('--habitue-text-secondary', colors.textSecondary);
    root.style.setProperty('--habitue-gold', colors.gold);
    root.style.setProperty('--habitue-gold-hover', colors.goldHover);
    
    // Save to localStorage
    localStorage.setItem('habitue-theme', theme);
    
    // Also set meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', colors.background);
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const stored = localStorage.getItem('habitue-theme-manual');
      if (!stored) {
        setTheme(e.matches ? 'light' : 'dark');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    // Mark as manually set
    localStorage.setItem('habitue-theme-manual', 'true');
  };

  const setThemeMode = (mode) => {
    if (mode === 'dark' || mode === 'light') {
      setTheme(mode);
      localStorage.setItem('habitue-theme-manual', 'true');
    } else if (mode === 'system') {
      localStorage.removeItem('habitue-theme-manual');
      const prefersDark = !window.matchMedia('(prefers-color-scheme: light)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
    }
  };

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    colors: themes[theme].colors,
    toggleTheme,
    setTheme: setThemeMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
